import { useCallback } from 'react';
import { apiClient } from '../../../services/apiClient';
import type { RecommendationSubjectType } from './useRecommendations';

/**
 * 推奨の採用/解除ログ (計画書§8 P4)。
 * POST /generation/recommendation-events の surface/action。バックエンドの
 * backend/services/recommendation/events_service.py の enum と同期させること。
 */
export type RecommendationEventSurface =
  | 'attribute_badge'
  | 'ai_omakase'
  | 'express_tone'
  | 'scenario_tone'
  | 'scenario_visual_style'
  | 'scenario_duration'
  | 'purpose_banner'
  | 'purpose_preset';

export type RecommendationEventAction = 'adopted' | 'cleared' | 'random_fallback';

export type RecommendationEvent = {
  subject_type?: RecommendationSubjectType | null;
  subject_id?: string | null;
  surface: RecommendationEventSurface;
  action: RecommendationEventAction;
  group_key?: string | null;
  option_key?: string | null;
  strength?: 'strong' | 'suggested' | null;
  score?: number | null;
  profile_status?: string | null;
  metadata?: Record<string, unknown>;
};

/**
 * 推奨の採用/解除を記録するフック。
 *
 * **計測は絶対に生成体験を阻害しない**: 呼び出し側は await せず「発火して忘れる」
 * (fire-and-forget) 使い方をすること。失敗は握りつぶし、開発時のデバッグ用に
 * console.debug のみ出す (トーストやエラー表示は一切しない)。
 */
export function useRecommendationEvents() {
  const logRecommendationEvent = useCallback((event: RecommendationEvent) => {
    apiClient.post('/generation/recommendation-events', event).catch((err) => {
      console.debug('[useRecommendationEvents] failed to log event (ignored):', event.surface, event.action, err);
    });
  }, []);

  return { logRecommendationEvent };
}
